import {
    branchConditions,
    branchConditionsDescriptions,
    branchConditionsFlags,
    directions,
    directionsDescriptions,
    fromSizesToString,
    instructionsDocumentationList,
    M68KDirectiveDocumentationList,
    type Size
} from '#lib/languages/M68K/M68K-documentation.ts'
import {
    m68kDialectHas,
    m68kDialectKey,
    type M68kDialect,
    type M68kNeeds
} from '#lib/languages/M68K/m68kDialect.ts'
import {
    M68K_COLORS,
    M68K_KEY_CODE_DOCS,
    M68K_KEY_CODE_RULES,
    M68K_REJECTED_TRAP_TASKS,
    M68K_TRAP_DOCS,
    M68K_TRAP_GROUP_DOCS,
    type M68KTrapDoc,
    type M68KTrapGroup
} from '#lib/languages/M68K/M68K-traps.ts'
import {
    fillPlaceholders,
    names,
    proseEntries,
    summaryOf,
    type Chapter,
    type DocumentationEntry,
    type EntryField,
    type EntryKind,
    type EntrySwatch
} from '../entries'
import { m68kInstructionCategories } from './categories'
import { m68kFormTags } from '../instructions/formTags'
import { withInstructionCategory } from '../instructions/resolve'
import { documentSignature } from '../instructions/document'
import { m68kDocumentForms, m68kInstructionDocument } from './instructionDocument'
import addressingModesMarkdown from './addressing-modes.md?raw'
import assemblerFeaturesMarkdown from './assembler-features.md?raw'
import conditionCodesMarkdown from './condition-codes.md?raw'
import exceptionsMarkdown from './exceptions.md?raw'
import shiftDirectionsMarkdown from './shift-directions.md?raw'
import trapsMarkdown from './traps.md?raw'
import { synthEntries } from '../synth'
import type { ProgramDocumentation } from '../documentation'

/**
 * The M68K Documentation. Instructions, directives, condition codes and trap tasks come from the
 * same tables the editor's hovers and the agent's prompt read; the explanations around them are the
 * markdown files beside this one, and the lists and tables inside those are filled in from the
 * tables too, so the text cannot drift from what the editor does. An instruction's forms, which the
 * hand-written tables do not list, come from s68k's own instruction table.
 */

const BASE = '/documentation/m68k'

/** The sizes an instruction or a directive takes, as the row shows them: `b, w, l`. */
function sizesOf(sizes: Size[]): string | undefined {
    return sizes.length > 0 ? fromSizesToString(sizes) : undefined
}

/**
 * A markdown file's sections as entries. The summary skips a quote's `>`, which `summaryOf` keeps:
 * the Exceptions open with a callout, and its first sentence is the one line a row should show.
 */
function prose(
    chapter: string,
    chapterHref: string,
    markdown: string,
    kind?: EntryKind,
    openingTitle?: string
): DocumentationEntry[] {
    return proseEntries({
        language: 'm68k',
        chapter,
        chapterHref,
        markdown,
        kind,
        openingTitle
    }).map((entry) => ({
        ...entry,
        summary: summaryOf(entry.searchText.replace(/^\s*>\s?/gm, ''))
    }))
}

/**
 * The Instructions Chapter; with a `dialect`, as a program written for it reads it: each
 * instruction with only the forms it can write, and without those it can write in none
 * ([m68k-machine.md](../../../../../../docs/design/m68k-machine.md), decision 36).
 */
function instructions(dialect?: M68kDialect): Chapter {
    const href = `${BASE}/instruction`
    const entries = instructionsDocumentationList.flatMap((instruction): DocumentationEntry[] => {
        //the forms of its rows, whose shared tags are the entry's
        const forms = m68kDocumentForms(instruction.name, dialect)
        const document = m68kInstructionDocument(instruction.name, dialect)
        if (!document) return []
        const entry = withInstructionCategory(
            {
                id: `m68k/instructions/${instruction.name}`,
                language: 'm68k',
                chapter: 'instructions',
                kind: 'instruction',
                title: instruction.name,
                //a family is one entry named by every mnemonic it stands for, so `beq` finds Bcc
                names: names(instruction.name, ...(instruction.compundNames ?? [])),
                signature: documentSignature(document),
                summary: document.summary,
                href: `${href}/${instruction.name}`,
                anchor: instruction.name,
                view: {
                    type: 'instruction',
                    document,
                    example: instruction.interactiveExample?.code
                },
                searchText: [document.longName, instruction.description].join('\n'),
                code: instruction.example
            },
            m68kInstructionCategories[instruction.name],
            forms.map(m68kFormTags)
        )
        return [entry]
    })
    return {
        id: 'instructions',
        language: 'm68k',
        title: 'Instructions',
        href,
        description: 'Supported instructions, operand sizes and addressing modes.',
        entries
    }
}

/**
 * Each addressing mode as the instructions' operand lists write it, by its anchor in
 * addressing-modes.md: the row's signature, and the spellings an exact search finds it by.
 */
const ADDRESSING_MODE_SYNTAX: Record<string, string[]> = {
    overview: ['<ea>'],
    direct: ['Dn', 'An', 'Rn'],
    indirect: ['(An)'],
    'address-register-displacement': ['d(An)'],
    'indirect-post-pre-increment': ['(An)+', '-(An)'],
    immediate: ['#imm', 'Im', '#<num>'],
    'effective-address': ['abs', 'Ea/<label>'],
    'address-register-indexed': ['d(An,Xn)'],
    'program-counter-relative': ['d(PC)', 'd(PC,Xn)'],
    'status-registers': ['sr', 'ccr'],
    'user-stack-pointer': ['usp'],
    'control-registers': ['Rc', 'vbr', 'sfc', 'dfc', 'msp', 'isp', 'cacr', 'caar'],
    //the 68020's, under `opt p=68020`
    68020: ['opt p=68020'],
    'scaled-index': ['(bd,An,Xn*k)', '(bd,PC,Xn*k)'],
    'base-displacements': ['(bd.w,An,Xn)', '(bd.l,An,Xn)'],
    'memory-indirect': ['([bd,An],Xn,od)', '([bd,An,Xn],od)', '([bd,PC],Xn,od)', '([bd,PC,Xn],od)'],
    'suppressed-registers': ['za0', 'zd0', 'zpc'],
    //not `bra.l` alone, which an exact search would take from `bra`'s own page
    'long-branches': ['bra.l label', 'bsr.l label', 'b<cc>.l label'],
    //the 68020's instructions' operands, as their pages and s68k's diagnostics write them
    'register-pairs': ['Dh:Dl', 'Dr:Dq', '(Rn1):(Rn2)', 'Dx:Dy', '(Rx):(Ry)'],
    'bit-fields': ['{offset:width}', 'opt bit']
}

function addressingModes(): Chapter {
    const href = `${BASE}/addressing-mode`
    const entries = prose(
        'addressing-modes',
        href,
        addressingModesMarkdown,
        'addressing-mode',
        'Effective addresses'
    ).map((entry): DocumentationEntry => {
        const syntax = ADDRESSING_MODE_SYNTAX[entry.anchor] ?? []
        return { ...entry, names: names(...syntax), signature: syntax.join(', ') || undefined }
    })
    return {
        id: 'addressing-modes',
        language: 'm68k',
        title: 'Addressing modes',
        href,
        description: 'The ways an operand names a register, a value or a place in memory.',
        entries
    }
}

function conditionCodes(): Chapter {
    const href = `${BASE}/condition-codes`
    const codes = branchConditions.map((code): DocumentationEntry => {
        const description = `${branchConditionsDescriptions.get(code) ?? code}.`
        //the four instructions that test it, which the explanation names only as families
        const [branch, loop, set, trap] = [`b${code}`, `db${code}`, `s${code}`, `trap${code}`].map(
            (name) => `[${name}](${BASE}/instruction/${name})`
        )
        const markdown = `${description}\n\nUsed by ${branch}, ${loop}, ${set} and, on the 68020, ${trap}.`
        return {
            id: `m68k/condition-codes/${code}`,
            language: 'm68k',
            chapter: 'condition-codes',
            kind: 'condition-code',
            title: code,
            names: names(code),
            signature: branchConditionsFlags.get(code),
            summary: description,
            href: `${href}#${code}`,
            anchor: code,
            view: { type: 'markdown', markdown },
            searchText: markdown
        }
    })
    return {
        id: 'condition-codes',
        language: 'm68k',
        title: 'Condition codes',
        href,
        description:
            'Status flags and the conditions tested by branches, conditional sets, loops and traps.',
        entries: [...prose('condition-codes', href, conditionCodesMarkdown), ...codes]
    }
}

function shiftDirections(): Chapter {
    const href = `${BASE}/shift-direction`
    const markdown = fillPlaceholders(shiftDirectionsMarkdown, {
        directions: directions
            .map((direction) => `- \`${direction}\`: ${directionsDescriptions.get(direction)}`)
            .join('\n')
    })
    return {
        id: 'shift-directions',
        language: 'm68k',
        title: 'Shift directions',
        href,
        description: 'Which way the shift and rotate instructions move the bits.',
        entries: prose('shift-directions', href, markdown)
    }
}

/** A `$00BBGGRR` equate as CSS, which wants the channels the other way round. */
function cssColor(color: number): string {
    return `rgb(${color & 0xff}, ${(color >> 8) & 0xff}, ${(color >> 16) & 0xff})`
}

function hexByte(code: number): string {
    return `$${code.toString(16).padStart(2, '0').toUpperCase()}`
}

/** The colors section of traps.md, with the assembly equates as swatches under its text. */
function withSwatches(section: DocumentationEntry): DocumentationEntry {
    const markdown = section.view.type === 'markdown' ? section.view.markdown : ''
    const swatches = Object.entries(M68K_COLORS).map(([name, color]): EntrySwatch => ({
        name: name.toLowerCase(),
        color: cssColor(color),
        value: `$${color.toString(16).padStart(8, '0').toUpperCase()}`
    }))
    return {
        ...section,
        //a program names them, so `red` opens the colors
        names: names(...swatches.map((swatch) => swatch.name)),
        view: { type: 'swatches', markdown, swatches },
        searchText: [markdown, ...swatches.map((swatch) => `${swatch.name} ${swatch.value}`)].join(
            '\n'
        )
    }
}

function groupEntry(
    group: (typeof M68K_TRAP_GROUP_DOCS)[number],
    href: string
): DocumentationEntry {
    const tasks = M68K_TRAP_DOCS.filter((task) => task.group === group.group).map(
        (task) => task.task
    )
    return {
        id: `m68k/trap-tasks/${group.group}`,
        language: 'm68k',
        chapter: 'trap-tasks',
        kind: 'prose',
        title: group.title,
        names: [],
        signature:
            tasks.length > 0 ? `tasks ${Math.min(...tasks)} to ${Math.max(...tasks)}` : undefined,
        summary: summaryOf(group.description),
        href: `${href}#${group.group}`,
        anchor: group.group,
        view: { type: 'markdown', markdown: group.description },
        searchText: group.description
    }
}

function taskEntry(task: M68KTrapDoc, href: string): DocumentationEntry {
    const fields: EntryField[] = []
    if (task.input) fields.push({ label: 'In', value: task.input })
    if (task.output) fields.push({ label: 'Out', value: task.output })
    if (task.deviation) fields.push({ label: 'Note', value: task.deviation })
    return {
        id: `m68k/trap-tasks/task-${task.task}`,
        language: 'm68k',
        chapter: 'trap-tasks',
        kind: 'trap-task',
        title: task.title,
        names: names(
            `task ${task.task}`,
            `trap 15 task ${task.task}`,
            `trap #15 task ${task.task}`,
            task.title
        ),
        signature: `D0.B = ${task.task}`,
        summary: summaryOf(task.description),
        href: `${href}#task-${task.task}`,
        anchor: `task-${task.task}`,
        view: { type: 'fields', markdown: task.description, fields },
        searchText: [
            `Trap #15 task ${task.task}.`,
            task.description,
            task.input ? `In: ${task.input}.` : '',
            task.output ? `Out: ${task.output}.` : '',
            task.deviation ?? ''
        ]
            .filter(Boolean)
            .join(' ')
    }
}

/**
 * The tasks that are not supported, one line per reason: neighbouring tasks that share one, such as
 * the eight network tasks, are listed together before it.
 */
function rejectedTaskList(): string {
    const lines: { tasks: string[]; reason: string }[] = []
    for (const task of M68K_REJECTED_TRAP_TASKS) {
        const named = `\`${task.task}\` ${task.title}`
        const last = lines[lines.length - 1]
        if (last?.reason === task.reason) last.tasks.push(named)
        else lines.push({ tasks: [named], reason: task.reason })
    }
    return lines.map((line) => `- ${line.tasks.join(', ')} — ${line.reason}`).join('\n')
}

/**
 * The Chapter in the page's order: the introduction, then each group of tasks after its own
 * introduction (the colors beside the graphics tasks, the key codes beside the keyboard ones, how
 * sound differs from EASy68K beside the sound ones), then the tasks that are not supported.
 */
function trapTasks(): Chapter {
    const href = `${BASE}/traps`
    const markdown = fillPlaceholders(trapsMarkdown, {
        keyCodeRules: M68K_KEY_CODE_RULES.map((rule) => `- ${rule}`).join('\n'),
        keyCodes: [
            '| Key | Code |',
            '| --- | ---- |',
            ...M68K_KEY_CODE_DOCS.map((key) => `| ${key.name} | \`${hexByte(key.code)}\` |`)
        ].join('\n'),
        unsupportedTasks: rejectedTaskList()
    })
    const sections = new Map(
        prose('trap-tasks', href, markdown).map((entry) => [entry.anchor, entry])
    )
    const take = (anchor: string): DocumentationEntry[] => {
        const entry = sections.get(anchor)
        sections.delete(anchor)
        return entry ? [entry] : []
    }
    const beside: Partial<Record<M68KTrapGroup, DocumentationEntry[]>> = {
        graphics: [...take('colors').map(withSwatches), ...take('pictures')],
        input: take('key-codes'),
        sound: take('sound-differences')
    }
    const closing = take('unsupported')
    const entries = [
        //what is left of traps.md introduces the whole Chapter
        ...sections.values(),
        ...M68K_TRAP_GROUP_DOCS.flatMap((group) => [
            groupEntry(group, href),
            ...(beside[group.group] ?? []),
            ...M68K_TRAP_DOCS.filter((task) => task.group === group.group).map((task) =>
                taskEntry(task, href)
            )
        ]),
        ...closing
    ]
    return {
        id: 'trap-tasks',
        language: 'm68k',
        title: 'Trap tasks',
        href,
        description:
            'Call an editor service with `trap #15`. Put its decimal task number in D0.B and use the registers listed for its inputs and results. `trap #0` through `trap #14` call handlers installed by the program.',
        entries
    }
}

function exceptions(): Chapter {
    const href = `${BASE}/exceptions`
    return {
        id: 'exceptions',
        language: 'm68k',
        title: 'Exceptions',
        href,
        description:
            'Exception vectors, handler entry and return, stack frames, processor modes, interrupts and cycle counts for the 68000, 68010 and 68020.',
        entries: prose('exceptions', href, exceptionsMarkdown)
    }
}

function directives(): Chapter {
    const href = `${BASE}/directive`
    const entries = M68KDirectiveDocumentationList.map((directive): DocumentationEntry => ({
        id: `m68k/directives/${directive.name}`,
        language: 'm68k',
        chapter: 'directives',
        kind: 'directive',
        title: directive.name,
        names: names(directive.name),
        signature: sizesOf(directive.sizes),
        summary: summaryOf(directive.description),
        href: `${href}#${directive.name}`,
        anchor: directive.name,
        view: {
            type: 'fields',
            markdown: directive.description,
            fields: [],
            example: directive.example
        },
        searchText: directive.description,
        code: directive.example
    }))
    return {
        id: 'directives',
        language: 'm68k',
        title: 'Directives',
        href,
        description:
            'Assembler directives for data, reserved space, constants, sections and included files.',
        entries
    }
}

function assemblerFeatures(): Chapter {
    const href = `${BASE}/assembler-features`
    return {
        id: 'assembler-features',
        language: 'm68k',
        title: 'Assembler features',
        href,
        description: 'What the assembler works out for you, such as arithmetic in operands.',
        entries: prose('assembler-features', href, assemblerFeaturesMarkdown)
    }
}

function synth(): Chapter {
    const href = `${BASE}/synth`
    return {
        id: 'synth',
        language: 'm68k',
        title: 'Synth',
        href,
        description:
            'Control eight sound channels through memory registers, with waveforms, volume envelopes, programmable wavetables and sample playback.',
        entries: synthEntries('M68K', 'm68k', href)
    }
}

let cached: Chapter[] | null = null

/** The M68K Documentation's Chapters. */
export function chapters(): Chapter[] {
    cached ??= [
        instructions(),
        addressingModes(),
        conditionCodes(),
        shiftDirections(),
        trapTasks(),
        synth(),
        exceptions(),
        directives(),
        assemblerFeatures()
    ]
    return cached
}

const THE_68010: M68kNeeds = { processor: '68010' }
const THE_68020: M68kNeeds = { processor: '68020' }

/**
 * The sections of the Chapters only a program for a later processor needs, by Chapter and anchor,
 * with what each needs. The Workbench's Documentation panel leaves them out for a program written
 * for an earlier processor ([m68k-machine.md](../../../../../../docs/design/m68k-machine.md),
 * decision 36); the site's pages show them all. The format error is the 68010's and the 68020's,
 * the control registers start with the 68010's three, and the bit fields are the 68020's and those
 * `opt bit` gives an earlier processor.
 */
export const M68K_LATER_PROCESSOR_SECTIONS: Readonly<
    Record<string, Readonly<Record<string, M68kNeeds>>>
> = {
    exceptions: {
        68010: THE_68010,
        'format-error': THE_68010,
        68020: THE_68020,
        'master-stack': THE_68020,
        'throwaway-frame': THE_68020,
        '68020-frames': THE_68020,
        '68020-trace': THE_68020,
        'odd-addresses': THE_68020,
        '68020-cycles': THE_68020
    },
    'addressing-modes': {
        'control-registers': THE_68010,
        68020: THE_68020,
        'scaled-index': THE_68020,
        'base-displacements': THE_68020,
        'memory-indirect': THE_68020,
        'suppressed-registers': THE_68020,
        'long-branches': THE_68020,
        'register-pairs': THE_68020,
        'bit-fields': { processor: '68020', bitField: true }
    }
}

/** Whether a program written for `dialect` reads a section: all but those a later processor needs. */
function sectionShown(entry: DocumentationEntry, dialect: M68kDialect): boolean {
    const needs = M68K_LATER_PROCESSOR_SECTIONS[entry.chapter]?.[entry.anchor]
    return !needs || m68kDialectHas(dialect, needs)
}

const byDialect = new Map<string, ProgramDocumentation>()

/**
 * The Chapters as the Workbench's Documentation panel shows them to a program written for
 * `dialect`, without what a later processor brought: the instructions it can write in no form, the
 * rows of the forms it cannot write, and the sections only a later processor needs; and the ids of
 * the entries left out, which its search results leave out too
 * ([m68k-machine.md](../../../../../../docs/design/m68k-machine.md), decision 36). A 68020 program
 * has everything.
 */
export function chaptersFor(dialect: M68kDialect): ProgramDocumentation {
    const key = m68kDialectKey(dialect)
    let made = byDialect.get(key)
    if (!made) {
        made = m68kDialectHas(dialect, THE_68020)
            ? { chapters: chapters(), hidden: new Set() }
            : leftOut(dialect)
        byDialect.set(key, made)
    }
    return made
}

/** The Chapters as a program for the 68000 or the 68010 reads them, and what they leave out. */
function leftOut(dialect: M68kDialect): ProgramDocumentation {
    const all = chapters()
    const shown = all.map((chapter) =>
        chapter.id === 'instructions'
            ? instructions(dialect)
            : {
                  ...chapter,
                  entries: chapter.entries.filter((entry) => sectionShown(entry, dialect))
              }
    )
    const kept = new Set(shown.flatMap((chapter) => chapter.entries.map((entry) => entry.id)))
    const hidden = new Set(
        all
            .flatMap((chapter) => chapter.entries.map((entry) => entry.id))
            .filter((id) => !kept.has(id))
    )
    return { chapters: shown, hidden }
}
